import React from 'react';
import { Link } from 'react-router-dom';
import { DynamicIcon } from '../components/icons/DynamicIcon';

const CookiePolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <DynamicIcon name="cookie" size={32} className="text-orange-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Política de Cookies</h1>
            </div>
            <Link
              to="/"
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <DynamicIcon name="arrowLeft" size={16} className="mr-2" />
              Voltar
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-6 sm:p-8">
          <div className="mb-8 text-center">
            <p className="text-sm text-gray-500">Última atualização: 29 de junho de 2025</p>
          </div>

          <div className="prose prose-orange max-w-none">
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. INTRODUÇÃO</h2>
            
            <p className="text-gray-700">
              Esta Política de Cookies explica como a EventPour ("nós", "nosso" ou "empresa") utiliza cookies e tecnologias similares para reconhecer, armazenar e gerenciar informações quando você visita nosso site e utiliza nossa plataforma.
            </p>
            
            <p className="text-gray-700">
              Cookies são pequenos arquivos de texto que são armazenados em seu dispositivo (computador, tablet, smartphone) quando você visita um site. Eles são amplamente utilizados para fazer os sites funcionarem de maneira mais eficiente, bem como fornecer informações aos proprietários do site.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">2. QUAIS COOKIES UTILIZAMOS</h2>
            
            <p className="text-gray-700">Utilizamos os seguintes tipos de cookies:</p>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">2.1 Cookies Necessários</h3>
            <p className="text-gray-700">
              Estes cookies são essenciais para o funcionamento básico do site. Eles permitem que você navegue pelo site e use recursos essenciais, como áreas seguras, carrinho de compras e faturamento eletrônico. Esses cookies não coletam informações sobre você que poderiam ser usadas para marketing ou lembrar onde você esteve na internet.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 my-4">
              <p className="text-orange-800 text-sm">
                <strong>Exemplos:</strong> cookies de sessão, cookies de autenticação, cookies de segurança
              </p>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">2.2 Cookies de Desempenho</h3>
            <p className="text-gray-700">
              Estes cookies coletam informações sobre como você usa nosso site, como quais páginas você visita com mais frequência e se encontra mensagens de erro. Eles não coletam informações que identificam você diretamente; todas as informações são agregadas e, portanto, anônimas. Usamos esses cookies para melhorar o desempenho do nosso site.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 my-4">
              <p className="text-orange-800 text-sm">
                <strong>Exemplos:</strong> Google Analytics, Hotjar, New Relic
              </p>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">2.3 Cookies Funcionais</h3>
            <p className="text-gray-700">
              Estes cookies permitem que o site forneça funcionalidades e personalização aprimoradas. Eles podem ser definidos por nós ou por provedores terceiros cujos serviços adicionamos às nossas páginas. Se você não permitir esses cookies, alguns ou todos esses serviços podem não funcionar corretamente.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 my-4">
              <p className="text-orange-800 text-sm">
                <strong>Exemplos:</strong> cookies de preferências de idioma, cookies de localização, cookies de personalização de interface
              </p>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">2.4 Cookies de Marketing</h3>
            <p className="text-gray-700">
              Estes cookies são usados para rastrear visitantes em sites. A intenção é exibir anúncios relevantes e envolventes para o usuário individual. Eles rastreiam seu navegador à medida que você visita diferentes sites para entender seus interesses e, em seguida, exibem anúncios relevantes para esses interesses.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 my-4">
              <p className="text-orange-800 text-sm">
                <strong>Exemplos:</strong> Facebook Pixel, Google Ads, LinkedIn Insight Tag
              </p>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">3. COOKIES ESPECÍFICOS QUE UTILIZAMOS</h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300 mt-4">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 border-b text-left">Nome do Cookie</th>
                    <th className="px-4 py-2 border-b text-left">Tipo</th>
                    <th className="px-4 py-2 border-b text-left">Finalidade</th>
                    <th className="px-4 py-2 border-b text-left">Duração</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-2 border-b">eventpour_session</td>
                    <td className="px-4 py-2 border-b">Necessário</td>
                    <td className="px-4 py-2 border-b">Mantém sua sessão ativa enquanto você navega pelo site</td>
                    <td className="px-4 py-2 border-b">Sessão</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border-b">eventpour_auth</td>
                    <td className="px-4 py-2 border-b">Necessário</td>
                    <td className="px-4 py-2 border-b">Gerencia seu estado de autenticação</td>
                    <td className="px-4 py-2 border-b">30 dias</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border-b">eventpour_preferences</td>
                    <td className="px-4 py-2 border-b">Funcional</td>
                    <td className="px-4 py-2 border-b">Armazena suas preferências de interface</td>
                    <td className="px-4 py-2 border-b">1 ano</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border-b">_ga, _gid</td>
                    <td className="px-4 py-2 border-b">Desempenho</td>
                    <td className="px-4 py-2 border-b">Google Analytics - Rastreia como você usa o site</td>
                    <td className="px-4 py-2 border-b">2 anos, 24 horas</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border-b">_fbp</td>
                    <td className="px-4 py-2 border-b">Marketing</td>
                    <td className="px-4 py-2 border-b">Facebook Pixel - Rastreia conversões e comportamento</td>
                    <td className="px-4 py-2 border-b">90 dias</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border-b">_hjid</td>
                    <td className="px-4 py-2 border-b">Desempenho</td>
                    <td className="px-4 py-2 border-b">Hotjar - Análise de comportamento do usuário</td>
                    <td className="px-4 py-2 border-b">1 ano</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border-b">eventpour_cookie_consent</td>
                    <td className="px-4 py-2 border-b">Necessário</td>
                    <td className="px-4 py-2 border-b">Armazena suas preferências de cookies</td>
                    <td className="px-4 py-2 border-b">1 ano</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">4. COMO GERENCIAR COOKIES</h2>
            
            <p className="text-gray-700">
              Você pode controlar e gerenciar cookies de várias maneiras. Lembre-se de que remover ou bloquear cookies pode impactar sua experiência de usuário e partes do nosso site podem não funcionar corretamente.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">4.1 Preferências de Cookies na EventPour</h3>
            <p className="text-gray-700">
              Você pode gerenciar suas preferências de cookies diretamente em nosso site através do banner de cookies ou clicando no ícone de cookies no canto inferior esquerdo de qualquer página.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">4.2 Configurações do Navegador</h3>
            <p className="text-gray-700">
              A maioria dos navegadores permite que você veja quais cookies você tem e exclua-os individualmente ou bloqueie cookies de sites específicos ou todos os sites. Esteja ciente de que se você excluir todos os cookies, todas as suas preferências serão perdidas, incluindo a preferência de não usar cookies.
            </p>
            
            <p className="text-gray-700 mt-4">
              Você pode encontrar informações sobre como gerenciar cookies nos navegadores mais populares nos links abaixo:
            </p>
            
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><a href="https://support.google.com/chrome/answer/95647" className="text-orange-600 hover:text-orange-800" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/pt-BR/kb/ative-e-desative-os-cookies-que-os-sites-usam" className="text-orange-600 hover:text-orange-800" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/pt-br/guide/safari/sfri11471/mac" className="text-orange-600 hover:text-orange-800" target="_blank" rel="noopener noreferrer">Safari</a></li>
              <li><a href="https://support.microsoft.com/pt-br/microsoft-edge/excluir-cookies-no-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" className="text-orange-600 hover:text-orange-800" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">4.3 Ferramentas de Terceiros</h3>
            <p className="text-gray-700">
              Você também pode gerenciar cookies de rastreamento de anúncios através de ferramentas como:
            </p>
            
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><a href="https://optout.aboutads.info/" className="text-orange-600 hover:text-orange-800" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance</a></li>
              <li><a href="https://www.youronlinechoices.com/" className="text-orange-600 hover:text-orange-800" target="_blank" rel="noopener noreferrer">Your Online Choices (EU)</a></li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">5. COOKIES E TECNOLOGIAS SIMILARES DE TERCEIROS</h2>
            
            <p className="text-gray-700">
              Alguns cookies são colocados por serviços de terceiros que aparecem em nossas páginas. Não temos controle sobre esses cookies. Eles são gerenciados pelos respectivos terceiros e são regidos pelas políticas de privacidade desses terceiros.
            </p>
            
            <p className="text-gray-700 mt-4">
              Utilizamos os seguintes serviços de terceiros que podem colocar cookies em seu dispositivo:
            </p>
            
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Google Analytics:</strong> para análise de uso do site</li>
              <li><strong>Facebook Pixel:</strong> para rastreamento de conversões e marketing</li>
              <li><strong>Hotjar:</strong> para análise de comportamento do usuário</li>
              <li><strong>Stripe:</strong> para processamento de pagamentos</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">6. COOKIES E PRIVACIDADE</h2>
            
            <p className="text-gray-700">
              Para informações mais detalhadas sobre como tratamos seus dados pessoais, consulte nossa <a href="https://politica-de-privacidade-eventpour.netlify.app/" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-800">Política de Privacidade</a>.
            </p>
            
            <p className="text-gray-700 mt-4">
              Nosso uso de cookies está em conformidade com a Lei Geral de Proteção de Dados (LGPD) e outras leis de privacidade aplicáveis. Obtemos seu consentimento antes de utilizar cookies não essenciais e fornecemos opções para controlar suas preferências.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">7. ALTERAÇÕES NESTA POLÍTICA</h2>
            
            <p className="text-gray-700">
              Podemos atualizar esta Política de Cookies periodicamente para refletir mudanças em nossas práticas ou por outros motivos operacionais, legais ou regulatórios. Notificaremos você sobre quaisquer alterações materiais através de um aviso em nosso site antes que as alterações entrem em vigor.
            </p>
            
            <p className="text-gray-700 mt-4">
              Recomendamos que você revise esta política regularmente para estar informado sobre como utilizamos cookies.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">8. CONTATO</h2>
            
            <p className="text-gray-700">
              Se você tiver dúvidas, preocupações ou solicitações relacionadas a esta Política de Cookies, entre em contato conosco:
            </p>
            
            <div className="bg-orange-50 p-4 rounded-lg mt-4">
              <p className="text-gray-700">
                <strong>E-mail:</strong> privacy@eventpour.com.br<br />
                <strong>Endereço:</strong> [Endereço completo]<br />
                <strong>Telefone:</strong> [Número de telefone]
              </p>
            </div>

            {/* Seção final */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-center text-gray-700 font-semibold">
                EventPour Tecnologia Ltda.<br />
                CNPJ: XX.XXX.XXX/0001-XX<br />
                Email: privacy@eventpour.com.br
              </p>
              
              <p className="text-center text-gray-600 mt-4 italic">
                Esta Política de Cookies foi atualizada pela última vez em 29 de junho de 2025.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <DynamicIcon name="glasses" size={24} className="text-blue-600 mr-2" />
              <span className="font-bold text-gray-900">EventPour</span>
            </div>
            
            <div className="flex space-x-6 text-sm">
              <a href="https://politica-de-privacidade-eventpour.netlify.app/" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-900">Política de Privacidade</a>
              <a href="https://termos-de-uso-eventpour.netlify.app/" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-900">Termos de Uso</a>
              <span className="text-gray-900 font-medium">Política de Cookies</span>
            </div>
          </div>
          
          <div className="mt-8 text-center text-sm text-gray-500">
            &copy; {new Date().getFullYear()} EventPour. Todos os direitos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicyPage;